import { array, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import entrepreneurAgentCheckAction from './entrepreneurAgentCheck.action.json' with { type: 'json' }

const requestState = enumerated({
  searchingAgents: 'searching_agents',
  agentAccepted: 'agent_accepted',
  agentArrived: 'agent_arrived',
  inAttendance: 'in_attendance',
  allAgentsRefused: 'all_agents_refused',
  noAgentsAvailable: 'no_agents_available',
  done: 'done',
  canceled: 'canceled',
  unknown: 'unknown',
})
const agentPosition = object({
  cpf: string(),
  latitude: number().isOptional(),
  longitude: number().isOptional(),
  accuracy: number().isOptional(),
  capturedAt: dateTime().isOptional(),
  lastGeolocationUpdate: dateTime().isOptional(),
})
const agentPreview = object({
  ...agentPosition.shape,
  geodesicDistanceMeters: number().isOptional(),
  drivingDistanceMeters: number().isOptional(),
  drivingDurationSeconds: number().isOptional(),
  status: enumerated({
    waitingAnswer: 'waiting_answer',
    accepted: 'accepted',
    refused: 'refused',
  }),
})
const agent = object({
  ...agentPosition.shape,
  geodesicDistanceMeters: number().isOptional(),
  drivingDistanceMeters: number().isOptional(),
  drivingDurationSeconds: number().isOptional(),
  status: enumerated({
    accepted: 'accepted',
  }),
})

export const entrepreneurAgentCheckInput = object({
  entrepreneurCpf: string(),
  mapProvider: enumerated({
    none: 'none',
    google_maps: 'google_maps',
    mapbox: 'mapbox',
    apple_maps: 'apple_maps',
    openstreetmap: 'openstreetmap',
    other: 'other',
  }).isOptional(),
})

export const entrepreneurAgentCheckOutput = object({
  state: requestState,
  pendingCount: number().isOptional(),
  refusedCount: number().isOptional(),
  agent: agent.isOptional(),
  agentsPreview: array(agentPreview).isOptional(),
  request: object({
    latitude: number().isOptional(),
    longitude: number().isOptional(),
    accuracy: number().isOptional(),
    legalEntityCnpj: string().isOptional(),
    address: object({
      displayAddress: string().isOptional(),
      postalCode: string().isOptional(),
      stateAbbreviation: string().isOptional(),
      city: string().isOptional(),
      cityId: number().isOptional(),
      neighborhood: string().isOptional(),
      street: string().isOptional(),
      number: string().isOptional(),
      complement: string().isOptional(),
      reference: string().isOptional(),
    }),
    companyName: string().isOptional(),
    contactPhone: string().isOptional(),
    serviceReason: string().isOptional(),
    cancellation: object({
      closedBy: enumerated({
        entrepreneur: 'entrepreneur',
        agent: 'agent',
        system: 'system',
      }).isOptional(),
      reasonCode: string().isOptional(),
      reasonDetail: string().isOptional(),
    }).isOptional(),
  }).isOptional(),
  route: object({
    status: enumerated({
      calculated: 'calculated',
      unavailable: 'unavailable',
    }).isOptional(),
    distanceMeters: number().isOptional(),
    durationSeconds: number().isOptional(),
    geometry: object({
      type: string(),
      coordinates: array(array(number())),
    }).isOptional(),
    calculatedAt: dateTime().isOptional(),
    agentLocationCapturedAt: dateTime().isOptional(),
    estimatedArrivalAt: dateTime().isOptional(),
  }).isOptional(),
  arrivalEstimate: object({
    source: enumerated({ google_maps: 'google_maps', osrm: 'osrm' }),
    calculatedAt: dateTime(),
    estimatedArrivalAt: dateTime(),
    attribution: string().isOptional(),
    displayPolicy: object({
      allowedMapProviders: array(string()),
      storage: enumerated({ no_store: 'no_store' }),
    }).isOptional(),
  }).isOptional(),
  confirmationCode: string().isOptional(),
  timestamps: object({
    createdAt: dateTime(),
    acceptedAt: dateTime().isOptional(),
    arrivedAt: dateTime().isOptional(),
    attendanceStartedAt: dateTime().isOptional(),
    closedAt: dateTime().isOptional(),
    updatedAt: dateTime(),
  }).isOptional(),
  warnings: array(
    object({
      code: enumerated({
        agentPositionUnavailable: 'AGENT_POSITION_UNAVAILABLE',
        routeUnavailable: 'ROUTE_UNAVAILABLE',
        unknownRequestState: 'UNKNOWN_REQUEST_STATE',
        googleArrivalUnavailable: 'GOOGLE_ARRIVAL_UNAVAILABLE',
        googleArrivalQuotaExceeded: 'GOOGLE_ARRIVAL_QUOTA_EXCEEDED',
      }),
      message: string(),
    }),
  ).isOptional(),
})

const entrepreneurAgentCheckDefinition = defineAction({
  ...entrepreneurAgentCheckAction,
  userActivityLog: true,
  operation: 'read' as const,
  input: entrepreneurAgentCheckInput,
  output: entrepreneurAgentCheckOutput,
})

export default entrepreneurAgentCheckDefinition
